/**
 * PortalEmailsPage
 *
 * Lists the business emails we've sent the logged-in investor (drawdown
 * notices, distributions, ad-hoc fund emails) with a delivery status, newest
 * first. Clicking a row opens the full rendered email.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AlertCircle, Loader2, Mail, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TransactionPagination } from '@/components/general-ledger/TransactionPagination';
import { useEmails } from '../hooks/useEmails';
import { EmailRow } from '../components/EmailRow';

const PAGE_SIZE = 25;

function PageHeader({ subtitle }: { subtitle: string }) {
  return (
    <div>
      <h1 className="text-3xl font-bold text-foreground">Emails</h1>
      <p className="mt-1 text-muted-foreground">{subtitle}</p>
    </div>
  );
}

export function PortalEmailsPage() {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Debounce the search box, then page through the matches on the server.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error } = useEmails({
    page,
    page_size: PAGE_SIZE,
    search,
  });

  const items = data?.items ?? [];
  const totalCount = data?.total_count ?? 0;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader subtitle="Emails we've sent you" />
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader subtitle="Emails we've sent you" />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="mb-4 h-12 w-12 text-destructive" />
            <h3 className="mb-2 text-lg font-medium text-foreground">
              Error Loading Emails
            </h3>
            <p className="max-w-md text-center text-muted-foreground">
              There was a problem loading your emails. Please try again later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // True empty inbox (no search active) - invite, no search box.
  if (totalCount === 0 && !search) {
    return (
      <div className="space-y-6">
        <PageHeader subtitle="Emails we've sent you" />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Mail className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium text-foreground">
              No Emails Yet
            </h3>
            <p className="max-w-md text-center text-muted-foreground">
              Emails we send you - such as drawdown notices and distributions -
              will appear here.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          subtitle={`${totalCount} ${totalCount === 1 ? 'email' : 'emails'}`}
        />
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search emails..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Search className="mb-4 h-12 w-12 text-muted-foreground" />
              <p className="text-muted-foreground">
                No emails match &quot;{search}&quot;
              </p>
            </div>
          ) : (
            items.map((email) => (
              <EmailRow
                key={email.email_queue_id}
                email={email}
                onClick={(id) => navigate(`/portal/emails/${id}`)}
              />
            ))
          )}
        </CardContent>
      </Card>

      <TransactionPagination
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={totalCount}
        onPageChange={setPage}
      />
    </div>
  );
}
